'use client'

import { useState, useMemo, useRef } from 'react'
import Link from 'next/link'

type Term = {
  term: string
  slug: string
  shortDefinition: string
  phonetic?: string | null
}

const ALPHABET = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

export default function DictionaryIndex({ terms }: { terms: Term[] }) {
  const [search, setSearch] = useState('')
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Filter terms by search
  const filtered = useMemo(() => {
    if (!search.trim()) return terms
    const q = search.toLowerCase()
    return terms.filter((t) => t.term.toLowerCase().includes(q))
  }, [terms, search])

  // Group by first letter
  const grouped = useMemo(() => {
    const map: Record<string, Term[]> = {}
    for (const t of filtered) {
      const first = t.term[0]?.toUpperCase() || '#'
      const key = /^[A-Z]$/.test(first) ? first : '#'
      if (!map[key]) map[key] = []
      map[key].push(t)
    }
    return map
  }, [filtered])

  const activeLetters = useMemo(() => new Set(Object.keys(grouped)), [grouped])

  function scrollToLetter(letter: string) {
    const el = sectionRefs.current[letter]
    if (el) {
      const offset = 60
      const top = el.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  return (
    <>
      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search terms..."
            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm pl-10 pr-10 py-2.5 outline-none focus:border-zinc-600 placeholder:text-zinc-600 transition-colors"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {search && (
          <p className="text-zinc-500 text-xs mt-2">
            {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
          </p>
        )}
      </div>

      {/* A-Z navigation bar */}
      <div className="sticky top-0 z-10 -mx-4 px-4 bg-zinc-900 border-y border-zinc-800 mb-10">
        <div className="flex items-center gap-0 overflow-x-auto py-2.5 scrollbar-hide">
          {ALPHABET.map((letter) => {
            const hasTerms = activeLetters.has(letter)
            return (
              <button
                key={letter}
                onClick={() => hasTerms && scrollToLetter(letter)}
                disabled={!hasTerms}
                className={`shrink-0 w-9 h-9 flex items-center justify-center text-sm font-semibold font-[family-name:var(--font-saira)] transition-colors
                  ${hasTerms ? 'text-white hover:text-[#8B7CF6] cursor-pointer' : 'text-zinc-700 cursor-default'}
                `}
              >
                {letter}
              </button>
            )
          })}
        </div>
      </div>

      {/* Term sections */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-zinc-500 text-sm">No terms found for "{search}"</p>
          <button
            onClick={() => setSearch('')}
            className="text-[#8B7CF6] text-sm mt-2 hover:underline"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="space-y-12">
          {ALPHABET.map((letter) => {
            const letterTerms = grouped[letter]
            if (!letterTerms || letterTerms.length === 0) return null
            return (
              <div
                key={letter}
                ref={(el) => { sectionRefs.current[letter] = el }}
                id={`letter-${letter}`}
              >
                <h2 className="text-[#8B7CF6] text-3xl font-bold font-[family-name:var(--font-saira)] mb-5 pb-2 border-b border-zinc-800">
                  {letter}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5">
                  {letterTerms.map((t) => (
                    <Link
                      key={t.slug}
                      href={`/terms/${t.slug}`}
                      className="text-zinc-300 text-sm hover:text-[#8B7CF6] transition-colors leading-relaxed"
                    >
                      {t.term}
                    </Link>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}